import { useMemo, useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import useFormDraft from '../../../../Components/Hooks/useFormDraft';
import { toStringId, formatDate, getCurrentUser, serializeFormValues } from './utils';

const selectorBreachRecord = createSelector(
    state => state.BreachRecord,
    data => ({ isAdding: data.isAdding, isUpdating: data.isUpdating })
);

const useIncumplimientoForm = ({
    dataIncumplimiento,
    dataFiscalizacion,
    initialFormValues,
    isEdit,
    handleSave,
    tipoFiscalizacion,
    isEnRevision,
    inspectionTypeOptions,
    inspectionAuthorityOptions,
    responsibleOptions,
    breachResponsibleAreaOptions,
    breachImpactOptions,
    breachOriginOptions,
    breachTypeOfEvidenceOptions,
    breachCategoryOptions,
    breachRiskClassificationOptions,
}) => {
    const { isAdding, isUpdating } = useSelector(selectorBreachRecord);
    const submittingRef = useRef(false);

    //Normalised option arrays
    const inspectionTypeData = useMemo(
        () => (inspectionTypeOptions || []).map(o => ({ value: toStringId(o.value), label: o.label || '' })),
        [inspectionTypeOptions]
    );
    const responsibleData = useMemo(
        () => (responsibleOptions || [])
            .map(o => ({ value: toStringId(o.value), label: o.label || '' }))
            .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })),
        [responsibleOptions]
    );
    const responsibleAreaData = useMemo(
        () => (breachResponsibleAreaOptions || []).map(o => ({ value: toStringId(o.value), label: o.label || '' })),
        [breachResponsibleAreaOptions]
    );
    const impactData = useMemo(
        () => (breachImpactOptions || []).map(o => ({ value: toStringId(o.value), label: o.label || '' })),
        [breachImpactOptions]
    );
    const originData = useMemo(
        () => (breachOriginOptions || []).map(o => ({ value: toStringId(o.value), label: o.label || '' })),
        [breachOriginOptions]
    );
    const evidenceData = useMemo(
        () => (breachTypeOfEvidenceOptions || []).map(o => ({ value: toStringId(o.value), label: o.label || '' })),
        [breachTypeOfEvidenceOptions]
    );
    const categoryData = useMemo(
        () => (breachCategoryOptions || []).map(o => ({ value: toStringId(o.value), label: o.label || '' })),
        [breachCategoryOptions]
    );
    const riskData = useMemo(
        () => (breachRiskClassificationOptions || []).map(o => ({ value: toStringId(o.value), label: o.label || '' })),
        [breachRiskClassificationOptions]
    );

    // In create mode: restrict authority options to those linked to the parent fiscalización.
    const inspectionAuthorityData = useMemo(() => {
        const allOptions = (inspectionAuthorityOptions || []).map(o => ({ value: toStringId(o.value), label: o.label || '' }));
        if (isEdit) return allOptions;
        const fiscalizacionAuthorityIds = (dataFiscalizacion?.entidades || []).map(toStringId);
        if (fiscalizacionAuthorityIds.length === 0) return allOptions;
        return allOptions.filter(opt => fiscalizacionAuthorityIds.includes(opt.value));
    }, [inspectionAuthorityOptions, isEdit, dataFiscalizacion]);

    //Draft
    // Key is scoped to the fiscalización + user so drafts from different inspections
    // never bleed into each other. Returns null (disables draft) when no ID available.
    const draftStorageKey = (!isEdit && dataFiscalizacion?._id)
        ? `breach_draft_${dataFiscalizacion._id}_${getCurrentUser()}`
        : null;
    const { draftData, saveDraft, clearDraft, hasDraft, lastSaved } = useFormDraft(draftStorageKey);

    // restoredValues holds the draft payload chosen by the user ("Continuar borrador").
    // Feeding it into initialValues lets enableReinitialize apply it safely, avoiding
    // race conditions that arise from calling setValues() directly.
    const [restoredValues, setRestoredValues] = useState(null);
    const [resumeDraftModalOpen, setResumeDraftModalOpen] = useState(false);
    const [discardDraftModalOpen, setDiscardDraftModalOpen] = useState(false);
    const [selectedClosureFile, setSelectedClosureFile] = useState(null);

    //Initial values
    const baseInitialValues = useMemo(() => ({
        description: dataIncumplimiento?.description || '',
        corrective_measure: dataIncumplimiento?.corrective_measure || '',
        inspection_type_id:
            toStringId(dataIncumplimiento?.inspection_type_id) ||
            toStringId(tipoFiscalizacion) ||
            '',
        breach_responsible_area_id:
            toStringId(dataIncumplimiento?.breach_responsible_area_id) || '',
        responsible_user_ids: (
            Array.isArray(dataIncumplimiento?.responsible_user_ids)
                ? dataIncumplimiento.responsible_user_ids
                : Array.isArray(dataIncumplimiento?.responsible_users) && dataIncumplimiento.responsible_users.length > 0
                    ? dataIncumplimiento.responsible_users.map(u => toStringId(u._id))
                    : dataIncumplimiento?.responsible_user_id
                        ? [toStringId(dataIncumplimiento.responsible_user_id)]
                        : []
        ).map(toStringId),
        breach_impact_id: toStringId(dataIncumplimiento?.breach_impact_id) || '',
        breach_origin_id: toStringId(dataIncumplimiento?.breach_origin_id) || '',
        breach_date: dataIncumplimiento?.breach_date ? formatDate(dataIncumplimiento.breach_date) : null,
        no_deadline: dataIncumplimiento?.no_deadline || false,
        breach_type_of_evidence_id: (
            Array.isArray(dataIncumplimiento?.breach_type_of_evidence_id)
                ? dataIncumplimiento.breach_type_of_evidence_id
                : dataIncumplimiento?.breach_type_of_evidence_id
                    ? [dataIncumplimiento.breach_type_of_evidence_id]
                    : []
        ).map(toStringId),
        breach_category_id: toStringId(dataIncumplimiento?.breach_category_id) || '',
        breach_risk_classification_id: toStringId(dataIncumplimiento?.breach_risk_classification_id) || '',
        inspection_authority_id: toStringId(dataIncumplimiento?.inspection_authority_id) || '',
    }), [dataIncumplimiento, tipoFiscalizacion]); // eslint-disable-line

    const initialValues = useMemo(() => {
        if (restoredValues) return restoredValues;
        if (!initialFormValues) return baseInitialValues;
        return {
            ...baseInitialValues,
            ...initialFormValues,
            responsible_user_ids: Array.isArray(initialFormValues?.responsible_user_ids)
                ? initialFormValues.responsible_user_ids.map(toStringId)
                : baseInitialValues.responsible_user_ids,
            breach_type_of_evidence_id: Array.isArray(initialFormValues?.breach_type_of_evidence_id)
                ? initialFormValues.breach_type_of_evidence_id.map(toStringId)
                : baseInitialValues.breach_type_of_evidence_id,
        };
    }, [restoredValues, initialFormValues, baseInitialValues]);

    //Formik
    const validation = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema: Yup.object({
            description: Yup.string().required('Campo requerido'),
            corrective_measure: Yup.string().required('Campo requerido'),
            inspection_type_id: Yup.string().required('Campo requerido'),
            breach_responsible_area_id: Yup.string().required('Campo requerido'),
            responsible_user_ids: Yup.array().min(1, 'Seleccione al menos un responsable').required('Campo requerido'),
            breach_impact_id: Yup.string(),
            breach_origin_id: Yup.string(),
            breach_date: Yup.date()
                .nullable()
                .when('no_deadline', {
                    is: true,
                    then: schema => schema.nullable().optional(),
                    otherwise: schema => schema.required('Campo requerido'),
                }),
            no_deadline: Yup.boolean(),
            breach_type_of_evidence_id: Yup.array().min(1, 'Seleccione al menos un tipo de evidencia').required('Campo requerido'),
            breach_category_id: Yup.string().required('Campo requerido'),
            breach_risk_classification_id: Yup.string().required('Campo requerido'),
            inspection_authority_id: Yup.string(),
        }),
        onSubmit: async values => {
            const d = values.breach_date;
            const dataToSave = {
                ...values,
                breach_date: !values.no_deadline && d instanceof Date
                    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                    : null,
                finding_closure_document: selectedClosureFile,
                finding_closure_file_id: dataIncumplimiento?.finding_closure_file_id ?? null,
            };
            await handleSave(dataToSave);
            clearDraft();
            setSelectedClosureFile(null);
            validation.resetForm();
        },
    });

    //Effects
    useEffect(() => {
        if (!isAdding && !isUpdating && !validation.isSubmitting) {
            submittingRef.current = false;
        }
    }, [isAdding, isUpdating, validation.isSubmitting]); // eslint-disable-line

    // Detect existing draft on mount and offer to resume (create mode only).
    useEffect(() => {
        if (isEdit || !hasDraft || !draftData) return;

        // When reopening from an internal modal handoff (e.g. create user),
        // the parent provides in-memory values and we should continue directly
        // without asking to resume draft.
        if (initialFormValues) return;

        setResumeDraftModalOpen(true);
    }, [isEdit, hasDraft, draftData, initialFormValues]);

    // Stable ref to saveDraft — avoids stale-closure and infinite-loop issues
    // because saveDraft from useFormDraft is not wrapped in useCallback.
    const saveDraftRef = useRef(saveDraft);
    useEffect(() => { saveDraftRef.current = saveDraft; }, [saveDraft]);

    // Auto-save on every form change (create mode only).
    // Guards: skip while resume modal is open (prevents overwriting the existing
    // draft before the user decides), and skip if the form is still blank.
    useEffect(() => {
        if (isEdit || resumeDraftModalOpen) return;
        const { description, corrective_measure, breach_responsible_area_id, responsible_user_ids } = validation.values;
        const hasFormData = Boolean(
            description ||
            corrective_measure ||
            breach_responsible_area_id ||
            responsible_user_ids?.length > 0
        );
        if (!hasFormData) return;
        const timer = setTimeout(() => {
            saveDraftRef.current({ formValues: serializeFormValues(validation.values) });
        }, 400);
        return () => clearTimeout(timer);
    }, [validation.values]); // eslint-disable-line

    //Draft handlers
    const handleResumeDraft = () => {
        if (!draftData?.formValues) return;
        setRestoredValues({
            ...draftData.formValues,
            breach_date: formatDate(draftData.formValues.breach_date),
        });
        setResumeDraftModalOpen(false);
    };

    const handleStartFresh = () => {
        clearDraft();
        setRestoredValues(null);
        setResumeDraftModalOpen(false);
    };

    const handleDiscardDraft = () => setDiscardDraftModalOpen(true);

    const handleConfirmDiscard = () => {
        clearDraft();
        setRestoredValues(null);
        setDiscardDraftModalOpen(false);
        validation.resetForm();
    };

    return {
        // Submission state
        isAdding,
        isUpdating,
        submittingRef,
        // Formik instance
        validation,
        // Normalised select data
        inspectionTypeData,
        responsibleData,
        responsibleAreaData,
        impactData,
        originData,
        evidenceData,
        categoryData,
        riskData,
        inspectionAuthorityData,
        // Draft UI state
        // Closure file
        selectedClosureFile,
        setSelectedClosureFile,
        // Draft UI state
        hasDraft,
        lastSaved,
        resumeDraftModalOpen,
        discardDraftModalOpen,
        setDiscardDraftModalOpen,
        // Draft handlers
        handleResumeDraft,
        handleStartFresh,
        handleDiscardDraft,
        handleConfirmDiscard,
    };
};

export default useIncumplimientoForm;
